<!-- Overrides @basecom-gmbh/pond/components/product/ProductAddToCart.vue -->
<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import { ApiClientError } from '@shopware/api-client';
import type { ResolvedApiError } from '~/types/errors';
import type { Schemas } from '@shopware/api-client/api-types';

const props = defineProps<{
    product: Schemas['Product'];
    icon?: boolean;
    label?: boolean;
}>();

const { product } = useProduct(props.product);
const customerStore = useCustomerStore();
const { signedIn } = storeToRefs(customerStore);
const showAddToCartButton = computed(() => {
    return (
        (product.value.customFields?.priceTransparency === 'SHOW_PRICE' || signedIn.value) &&
        product.value.translated.customFields?.priceAvailable
    );
});
const { addToCart, quantity, isInCart, count } = useAddToCart(product);
const { refreshCart, changeProductQuantity } = useCart();
const { trackAddToCart, trackRemoveFromCart } = useAnalytics();
quantity.value = count.value || product.value.minPurchase;
const { resolveApiErrors } = useApiErrorsResolver();
const { pushError, pushSuccess } = useNotifications();
const apiErrors = ref<ResolvedApiError[]>([]);
const { t } = useI18n();

const handleEnter = async $event => {
    if ($event.target !== null) {
        // remove focus from input to trigger quantity update
        $event.target.blur();
    }

    await handleAddToCart();
};

const handleAddToCart = async () => {
    try {
        // CUSTOM IDS-95: multiply based on quantity/package
        const quantityNumber = quantity.value ?? 1;
        await addToCart();
        trackAddToCart(product.value, quantityNumber);
        quantity.value = product.value.minPurchase;

        pushSuccess(t('product.addToCart.successMessage', { productName: product.value.translated.name }));
    } catch (error) {
        pushError(t('product.addToCart.errorMessage', { productName: product.value.translated.name }));

        if (error instanceof ApiClientError) {
            apiErrors.value = resolveApiErrors(error.details.errors, 'product');
            return;
        }

        apiErrors.value.push({ key: 'product', code: 'PRODUCT_ADD_TO_CART_GENERAL_ERROR' });
    }
};

//CUSTOM IDS-95
const updateQuantity = async (quantityInput: number | undefined) => {
    if (quantityInput === count.value) return;

    const addedProductsNumbers = Number(quantityInput) - count.value;

    try {
        const response = await changeProductQuantity({
            id: product.value.id,
            quantity: +Number(quantityInput),
        });
        if (addedProductsNumbers > 0) {
            trackAddToCart(product.value, addedProductsNumbers);
        } else {
            trackRemoveFromCart(product.value, Math.abs(addedProductsNumbers));
        }
        await refreshCart(response);

        pushSuccess(t('product.addToCart.successMessage', { productName: product.value.translated.name }));
    } catch (error) {
        pushError(t('product.addToCart.errorMessage', { productName: product.value.translated.name }));
    }
    // Make sure that quantity is the same as it is in the response
    quantity.value = count.value;
};

// allows the user to change the quantity multiple times before firing a single request
const debounceUpdate = useDebounceFn(updateQuantity, 600);
</script>

<template>
    
<FormKitLazyProvider config-file="true">
<FormKit
        v-if="product.available && showAddToCartButton"
        type="form"
        :actions="false"
        :classes="{
            form: 'flex justify-end',
            outer: 'w-20',
        }"
        @keydown.enter.prevent
    >
        <ul
            v-if="apiErrors.length"
            class="validation-errors text-status-danger"
        >
            <li
                v-for="(error, index) in apiErrors"
                :key="`product-error-${index}`"
            >
                {{ error.code }}
            </li>
        </ul>

        <div
            v-if="isInCart"
            class="flex h-[59px] items-center justify-center"
        >
            <SharedQuantityInput
                v-model="quantity"
                :product="product"
                @on-enter="handleEnter($event)"
                @on-update="debounceUpdate"
            />
        </div>
        <div v-else>
            <FormKitIcon
                class="cursor-pointer"
                icon="ids_addToCartButton"
                @click="handleAddToCart"
            />
        </div>
    </FormKit>
</FormKitLazyProvider>

</template>
